import React, { useState, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { PiArrowsDownUpLight, PiGearLight, PiFunnelLight, PiMagnifyingGlassLight, PiXLight, PiGridFourLight, PiSortAscendingLight, PiLockLight, PiArrowsClockwiseLight, PiTrashLight, PiKanbanLight } from 'react-icons/pi';
import clsx from 'clsx';
import Badge from './Badge';
import { listCountText } from './listCount';
import Button from './Button';
import SearchInput from './SearchInput';

type FilterOption = {
  value: string;
  label: string;
  checked?: boolean;
  children?: FilterOption[];
  parent_id?: number | null;
};

type FilterSection = {
  id: string;
  name: string;
  options: FilterOption[];
};

type SortOption = {
  value: string;
  label: string;
  current: boolean;
};

export type TabItem = {
  label: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
  icon?: React.ReactElement;
  lock?: boolean;
};

type FilterAction = {
  label: string;
  onClick: () => void;
  icon?: React.ReactElement;
};

/** Card · Table · (optional) stage Board. */
export type ListViewMode = 'grid' | 'table' | 'board';

export type ScopeOption = {
  value: string;
  /** Already-translated. */
  label: string;
  icon?: React.ReactElement;
};

export type ScopeSwitch = {
  value: string;
  options: ScopeOption[];
  onChange: (value: string) => void;
  /** Accessible name for the group. Already-translated. */
  label?: string;
};

type FilterBarWithTabsProps = {
  tabs?: TabItem[];
  rightTabs?: TabItem[];
  activeTab?: string;
  onTabClick?: (tabValue: string) => void;
  filters?: FilterSection[];
  sortOptions?: SortOption[];
  selectedFilters?: { [key: string]: string[] };
  onFilterChange?: (filterName: string, selectedValues: string[]) => void;
  onSortChange?: (value: string) => void;
  onSearchChange?: (searchQuery: string) => void;
  sortValue?: string;
  actions?: FilterAction[];
  onClearAllFilters?: () => void;
  additionalControls?: React.ReactNode;
  viewMode?: ListViewMode;
  /** Typed `any` so the many pages holding a `'grid' | 'table'` setter still fit. */
  setViewMode?: (mode: any) => void;
  onViewModeChange?: (mode: any) => void;
  /**
   * Which view modes the switch offers. Defaults to Card + Table, so every
   * existing caller is unchanged; a page with a stage board passes all three.
   */
  viewModes?: ListViewMode[];
  /**
   * SCOPE — which SET is being listed (Active ⟷ Archived), not which rows
   * within it. Rendered beside the view switch as compact pills.
   *
   * A dedicated prop rather than another entry in `filters` on purpose: scope
   * must survive "Clear all filters", because clearing a filter should never
   * silently move the user to a different list than the one they were reading.
   */
  scope?: ScopeSwitch;
  /**
   * Push the filter/sort/search cluster into the RIGHT group even when there
   * are no tabs, so the bar reads `… | count badge | search`.
   *
   * Without tabs the controls otherwise sit far left and the count badge far
   * right, which on a bar whose left side is empty reads as two unrelated
   * things at opposite ends of the screen. Opt-in, so every existing caller
   * is unchanged.
   */
  controlsOnRight?: boolean;
  noParentPadding?: boolean;
  noParentHeight?: boolean;
  noBorder?: boolean;
  onRefresh?: () => void;
  isRefreshLoading?: boolean;
  selectionCount?: number;
  /*
    "Showing 51–100 of 1037 matters" — the LEFT of the bar.

    It used to be drawn by `Table`, in a row of its own above the rows. This
    is where it belongs: the bar is where a list is narrowed, so it is where
    the size of what you narrowed it to should be read, and moving it here
    gave the table back a whole row of height.

    `itemCountLabel` is an i18n KEY (`matters`), not a translated string.
    `itemPage`/`itemPageSize` turn the count into a range — see `listCount`.
  */
  itemCount?: number;
  totalItemCount?: number;
  itemCountLabel?: string;
  itemPage?: number;
  itemPageSize?: number;
  /** Recycle bin toggle — shows a small bin button on the right side of the
   *  toolbar that flips between active and trashed views. The page owns the
   *  state and is responsible for fetching the right data set when the
   *  toggle changes. */
  recycleBinEnabled?: boolean;
  recycleBinActive?: boolean;
  onRecycleBinToggle?: (active: boolean) => void;
  recycleBinCount?: number;
  /** Resource label rendered as "Back to {recycleBinLabel}" when the bin
   *  view is active (e.g. "Matters", "Teams", "Playbooks"). When off, the
   *  button shows only the bin icon. */
  recycleBinLabel?: string;
};

const FilterBarWithTabs: React.FC<FilterBarWithTabsProps> = ({
  tabs = [],
  rightTabs = [],
  activeTab,
  onTabClick,
  filters = [],
  sortOptions = [],
  selectedFilters = {},
  onFilterChange,
  onSortChange,
  onSearchChange,
  sortValue,
  actions = [],
  onClearAllFilters,
  additionalControls,
  viewMode,
  setViewMode,
  onViewModeChange,
  viewModes,
  scope,
  controlsOnRight = false,
  noParentPadding = false,
  noParentHeight = false,
  noBorder = false,
  onRefresh,
  isRefreshLoading = false,
  selectionCount,
  itemCount,
  totalItemCount,
  itemCountLabel = 'items',
  itemPage,
  itemPageSize,
  recycleBinEnabled = false,
  recycleBinActive = false,
  onRecycleBinToggle,
  recycleBinCount,
  recycleBinLabel,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { t } = useTranslation();

  const totalActiveFilters = Object.values(selectedFilters).reduce((acc, curr) => acc + curr.length, 0);

  const hasControls = filters.length > 0 || sortOptions.length > 0 || onSearchChange || actions.length > 0 || additionalControls || viewMode || scope || rightTabs.length > 0 || onRefresh;

  const hasTabs = tabs.length > 0 || rightTabs.length > 0;

  const VIEW_MODE_META: Record<ListViewMode, { icon: React.ReactElement; title: string }> = {
    grid: { icon: <PiGridFourLight className="h-4 w-4 text-fg-body" />, title: t('Card View') },
    table: { icon: <PiSortAscendingLight className="h-4 w-4 text-fg-body" />, title: t('Table View') },
    board: { icon: <PiKanbanLight className="h-4 w-4 text-fg-body" />, title: t('Board View') },
  };

  const availableViewModes: ListViewMode[] = viewModes ?? ['grid', 'table'];

  // JSX variables, not components — a component defined in the render body
  // remounts on every keystroke and takes focus with it.
  const viewToggle = viewMode && setViewMode ? (
    <div data-tour-id="matters-view-toggle" className="flex items-center border border-hairline rounded bg-surface-sunken/70">
      {availableViewModes.map((mode, index) => (
        <button
          key={mode}
          onClick={() => setViewMode(mode)}
          aria-pressed={viewMode === mode}
          className={clsx(
            'p-1',
            index === 0 && 'rounded-l',
            index === availableViewModes.length - 1 && 'rounded-r',
            viewMode === mode ? 'bg-surface shadow-sm' : 'hover:bg-surface-sunken'
          )}
          title={VIEW_MODE_META[mode].title}
        >
          {VIEW_MODE_META[mode].icon}
        </button>
      ))}
    </div>
  ) : null;

  const scopeSwitch = scope && scope.options.length > 0 ? (
    <div
      role="group"
      aria-label={scope.label}
      className="glass-bar inline-flex items-center gap-0.5 rounded-full p-0.5 border border-hairline-soft shadow-sm"
    >
      {scope.options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={scope.value === option.value}
          onClick={() => { if (scope.value !== option.value) scope.onChange(option.value); }}
          className={clsx(
            'nav-pill flex items-center gap-1 rounded-full px-2.5 py-0.5',
            'text-[11px] font-medium transition-colors duration-200 ease-slide',
            scope.value === option.value
              ? 'nav-pill-active text-fg-strong'
              : 'text-fg-muted hover:text-fg-body'
          )}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  ) : null;

  // Helper function to flatten hierarchical options for label map
  const flattenOptions = (options: FilterOption[]): FilterOption[] => {
    return options.flatMap((option) => [option, ...(option.children ? flattenOptions(option.children) : [])]);
  };

  const filterLabelMap = filters.reduce((acc, section) => {
    const flatOptions = flattenOptions(section.options);
    flatOptions.forEach((option) => {
      acc[`${section.id}:${option.value}`] = `${t(section.name)}: ${t(option.label)}`;
    });
    return acc;
  }, {} as Record<string, string>);

  // Helper function to render filter options recursively with proper indentation
  const renderFilterOption = (option: FilterOption, section: FilterSection, depth: number = 0) => {
    const isChecked = selectedFilters[section.id]?.includes(option.value);
    const isParent = option.children && option.children.length > 0;
    const paddingLeft = depth > 0 ? `${depth * 16}px` : '0px';

    // If this is a parent option, render it as a header instead of a clickable item
    if (isParent && depth > 0) {
      return (
        <React.Fragment key={option.value}>
          <div
            className="px-4 py-2 text-xs text-fg-body bg-surface-sunken/80 font-medium"
            style={{ paddingLeft: `calc(1rem + ${paddingLeft})` }}
          >
            {t(option.label)}
          </div>
          {option.children && option.children.map((child) => renderFilterOption(child, section, depth + 1))}
        </React.Fragment>
      );
    }

    // If this is a root parent, render it as a header
    if (isParent && depth === 0) {
      return (
        <React.Fragment key={option.value}>
          <div className="px-4 py-2 text-xs text-fg-body bg-surface-sunken/80 font-medium">
            {t(option.label)}
          </div>
          {option.children && option.children.map((child) => renderFilterOption(child, section, depth + 1))}
        </React.Fragment>
      );
    }

    // Otherwise, render as a clickable checkbox option
    return (
      <React.Fragment key={option.value}>
        <Menu.Item>
          {({ active }) => (
            <div
              className={`flex items-center py-2 cursor-pointer text-xs transition-colors ${active ? 'bg-surface-soft' : ''}`}
              style={{ paddingLeft: `calc(1rem + ${paddingLeft})`, paddingRight: '1rem' }}
              onClick={() => {
                const current = selectedFilters[section.id] || [];
                const updated = isChecked
                  ? current.filter((v) => v !== option.value)
                  : [option.value]; // Single selection
                onFilterChange?.(section.id, updated);
              }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                readOnly
                className="h-4 w-4 accent-champagne border-hairline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
              />
              <span
                className="ml-3 text-fg-body truncate"
              >
                {t(option.label)}
              </span>
            </div>
          )}
        </Menu.Item>
        {option.children && option.children.map((child) => renderFilterOption(child, section, depth + 1))}
      </React.Fragment>
    );
  };

  const renderTab = (tab: TabItem) => {
    const isActive = activeTab === tab.value;
    const tabClasses = clsx(
      'nav-pill flex items-center px-3 py-1 text-xs font-medium rounded',
      isActive
        ? 'nav-pill-active text-fg-strong'
        : 'text-fg-muted hover:text-fg-body'
    );
    const disabledClasses = tab.disabled ? 'opacity-50 cursor-not-allowed' : '';
    return (
      <button
        key={tab.value}
        onClick={() => !tab.disabled && onTabClick?.(tab.value)}
        disabled={tab.disabled}
        title={tab.disabled ? tab.tooltip : ''}
        className={`${tabClasses} ${disabledClasses}`}
      >
        {tab.icon && <span className="mr-1">{tab.icon}</span>}
        {t(tab.label)}
        {tab.lock && (
          <PiLockLight
            className="absolute -top-2 -right-2 text-warning-500 bg-surface rounded-full shadow-md p-[2px]"
            size={16}
            title={t('Premium Feature')}
          />
        )}
      </button>
    );
  };

  // Shared button style for filter bar controls
  const controlBtnClass = "flex gap-1 items-center px-2 py-1 border border-hairline bg-transparent text-fg-body hover:bg-surface-sunken/70 hover:border-champagne/60 transition-all duration-200 rounded shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60";

  // When tabs are present, filter/sort/search controls move to the right side
  const hasLeftTabs = tabs.length > 0;

  /*
    SEARCH LEADS THE ROW.

    It used to render after Filter and Sort, which put the one control people
    reach for most at the far end of the cluster. It is the first thing in the
    fragment now, so every list page in the app reads Search → Filter → Sort →
    Clear All. Hoisted into its own variable purely so the ordering is a single
    line rather than a large block move.

    SEARCH OPENS OVER THE BAR, NOT INSIDE IT.

    The icon button used to be REPLACED by a `w-64` field in the same flex row,
    so opening search grew the row by ~14rem — pushing the other controls
    along, and on a narrow bar wrapping onto a second line, which moved
    everything below it. The button now keeps its slot at all times (it only
    goes `invisible`), and the field is absolutely positioned over the
    neighbouring controls. Nothing in the layout changes size, so nothing
    reflows: the bar keeps its height and the table below never jumps.
  */
  const searchControl = onSearchChange ? (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setIsSearchOpen(true)}
        aria-expanded={isSearchOpen}
        aria-label={t('Search')}
        className={clsx(controlBtnClass, isSearchOpen && 'invisible')}
        tabIndex={isSearchOpen ? -1 : 0}
      >
        <PiMagnifyingGlassLight className="h-4 w-4 text-fg-muted" />
      </button>
      {isSearchOpen && (
        <div
          className={clsx(
            // `bg-bg` so the controls it floats over do not read through it.
            'absolute top-1/2 -translate-y-1/2 z-20 w-64 max-w-[60vw] rounded-md bg-bg',
            'origin-left transition-all duration-200 ease-slide',
            // Grows away from the edge the controls are anchored to, so it
            // never runs off the bar. Leading the row, it opens rightwards on
            // an untabbed page and leftwards where the cluster sits right.
            hasLeftTabs ? 'right-0' : 'left-0'
          )}
        >
          <SearchInput
            autoFocus
            size="small"
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              onSearchChange?.(value);
            }}
            /* Closes again when it is empty and the user looks away, so
               the bar returns to the icon it started as. */
            onBlur={() => { if (!searchQuery) setIsSearchOpen(false); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchQuery('');
                onSearchChange?.('');
                setIsSearchOpen(false);
              }
            }}
          />
        </div>
      )}
    </div>
  ) : null;

  // Reusable filter/sort/search/clear controls block (JSX variable, not a component,
  // to avoid unmount/remount on every re-render which would lose input focus)
  const filterSortSearchControls = (
    <>
      {searchControl}
      {filters.length > 0 && onFilterChange && (
        <div className="flex items-center gap-2">
          <Menu as="div" className="relative">
            <Menu.Button className={controlBtnClass}>
              <PiFunnelLight className="h-4 w-4 text-fg-muted" />
              <span className="text-fg-body text-xs">{t('Filter')}</span>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Menu.Items className={clsx(
                "absolute mt-2 min-w-max max-w-96 glass rounded-md shadow-lg z-50 max-h-96 overflow-y-auto custom-scrollbar",
                hasLeftTabs ? "right-0" : "left-0"
              )}>
                {filters.map((section, sectionIndex) => (
                  <div key={section.id} className={sectionIndex > 0 ? 'border-t border-hairline' : ''}>
                    <div className="px-4 py-3 text-xs text-fg-strong bg-surface-sunken/80 sticky top-0">{t(section.name)}</div>
                    {section.options.map((option) => renderFilterOption(option, section, 0))}
                  </div>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
          {/*
            EACH CHIP REMOVES ITSELF.

            The only way out of a filter used to be "Clear All", so narrowing
            a list by three things and then wanting two of them meant starting
            again and re-picking both. A filter set is something people
            TAILOR, and the chip that says a filter is on is the obvious place
            to turn it off — so every chip carries its own ✕ and takes only
            itself with it.

            `Badge` already knew how to do this (`onClose`); the bar simply
            never asked.
          */}
          {totalActiveFilters > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {Object.entries(selectedFilters).flatMap(([sectionId, values]) =>
                values.map((value) => (
                  <Badge
                    key={`${sectionId}:${value}`}
                    color="gray"
                    className="text-xs"
                    onClose={() => onFilterChange?.(
                      sectionId,
                      (selectedFilters[sectionId] || []).filter((v) => v !== value),
                    )}
                  >
                    {filterLabelMap[`${sectionId}:${value}`] || `${t(sectionId)}: ${t(value)}`}
                  </Badge>
                ))
              )}
            </div>
          )}
        </div>
      )}
      {/* Table sorts from its own column headers; card and board views have none. */}
      {sortOptions.length > 0 && onSortChange && (viewMode === 'grid' || viewMode === 'board') && (
        <Menu as="div" className="relative">
          <Menu.Button className={controlBtnClass}>
            <PiArrowsDownUpLight className="h-4 w-4 text-fg-muted" />
            <span className="text-fg-body text-xs">{t('Sort')}</span>
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Menu.Items className={clsx(
              "absolute mt-2 w-56 glass rounded-md shadow-lg z-50",
              hasLeftTabs ? "right-0" : "left-0"
            )}>
              {sortOptions.map((option) => (
                <Menu.Item key={option.value}>
                  {({ active }) => (
                    <div
                      onClick={() => onSortChange(option.value)}
                      className={`p-2 cursor-pointer text-xs text-fg-body ${active ? 'bg-surface-soft' : ''}`}
                    >
                      {t(option.label)}
                    </div>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
      )}
      {/*
        NO "Clear All". Removing filters one at a time is what people
        actually do, and the chips above carry that now — a button that can
        only throw the whole set away was the reason a chip had no ✕ on it.
        `onClearAllFilters` stays in the props for callers that reset filters
        from their own UI; the bar just does not draw a button for it.
      */}
    </>
  );

  /*
    THE COUNT, and the hairline that separates it from the controls.

    First thing in the bar, on the left, because it is the answer to what the
    rest of the bar does. The separator only draws when something follows it —
    a rule with nothing on its right is a rule with nothing to separate.
  */
  const countText = listCountText(
    {
      showing: itemCount,
      total: totalItemCount ?? itemCount,
      label: itemCountLabel,
      selected: selectionCount,
      page: itemPage,
      pageSize: itemPageSize,
    },
    t,
  );
  const hasFollowingControls =
    tabs.length > 0 || hasControls || (!hasLeftTabs && !controlsOnRight);
  const countBadge = countText ? (
    <div className="flex min-w-0 items-center gap-3" data-testid="list-count">
      <Badge color="gray">{countText}</Badge>
      {hasFollowingControls && (
        <span aria-hidden className="hidden h-5 w-px flex-shrink-0 bg-hairline sm:block" />
      )}
    </div>
  ) : null;

  return (
    <div className={clsx(
      { 'border-b border-hairline': !noBorder },
      { 'px-4 pt-3 pb-2': !noParentPadding },
      { 'pb-2 px-2': noParentPadding },
    )}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {/* ── LEFT SIDE: how many → tabs → (controls when no tabs) ── */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          {countBadge}
          {tabs.length > 0 && (
            <div className="flex space-x-2">
              {tabs.map((tab) => renderTab(tab))}
            </div>
          )}
          {!hasLeftTabs && !controlsOnRight && filterSortSearchControls}
        </div>

        {/* ── RIGHT SIDE: (controls when tabs present) → Item Count → rightTabs → View Toggle → Actions → Additional → Refresh ── */}
        {hasControls && (
          /* WRAPS. The left group always could; this one could not, so on a
             narrow window its buttons ran past the bar's right edge instead of
             dropping to a second line. `justify-end` keeps the short last row
             tucked against the edge it belongs to. */
          <div className="flex flex-wrap items-center justify-end gap-3">
            {hasLeftTabs && filterSortSearchControls}
            {!hasLeftTabs && controlsOnRight && filterSortSearchControls}
            {rightTabs.length > 0 && (
              <div className="flex space-x-2">
                {rightTabs.map((tab) => renderTab(tab))}
              </div>
            )}
            {scopeSwitch}
            {viewToggle}
            {actions.length > 0 && selectionCount != null && selectionCount > 0 && (
              <Menu as="div" className="relative" data-tour-id="matters-actions">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-fg-muted whitespace-nowrap">
                    {selectionCount} {t('selected')}
                  </span>
                  <Menu.Button className="flex gap-1 items-center px-2 py-1 border border-champagne/40 bg-champagne/10 text-fg-strong hover:bg-champagne/20 transition-all duration-200 rounded shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60">
                  <PiGearLight className="h-4 w-4" />
                  <span className="text-xs">{t('Actions')}</span>
                </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 glass rounded-md shadow-lg z-50">
                    {actions.map((action, index) => (
                      <Menu.Item key={index}>
                        {({ active }) => (
                          <div
                            onClick={action.onClick}
                            className={`flex items-center gap-2 p-2 cursor-pointer text-xs text-fg-body ${active ? 'bg-surface-soft' : ''}`}
                          >
                            {action.icon && <span>{action.icon}</span>}
                            {t(action.label)}
                          </div>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
            {additionalControls && <div className="flex items-center gap-3">{additionalControls}</div>}
            {onRefresh && (
              <Button
                variant="outline"
                size="small"
                onClick={onRefresh}
                disabled={isRefreshLoading}
                icon={<PiArrowsClockwiseLight className={clsx('h-4 w-4 ml-1', isRefreshLoading && 'animate-spin')} />}
              >
                {t('Refresh')}
              </Button>
            )}
            {recycleBinEnabled && (
              <button
                type="button"
                onClick={() => onRecycleBinToggle?.(!recycleBinActive)}
                title={recycleBinActive
                  ? t('Back to {{label}}', { label: recycleBinLabel || t('list') })
                  : t('Open recycle bin')}
                className={clsx(
                  'inline-flex items-center gap-1 h-7 px-2 rounded border text-xs transition-colors',
                  recycleBinActive
                    ? 'bg-danger-50 dark:bg-danger-900/30 border-danger-300 dark:border-danger-700 text-danger-600 dark:text-danger-300'
                    : 'bg-transparent border-hairline text-fg-body hover:bg-surface-sunken/70'
                )}
              >
                <PiTrashLight className="h-4 w-4" />
                {recycleBinActive && (
                  <span>
                    {recycleBinLabel
                      ? t('Back to {{label}}', { label: recycleBinLabel })
                      : t('Recycle Bin')}
                  </span>
                )}
                {typeof recycleBinCount === 'number' && recycleBinCount > 0 && !recycleBinActive && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-medium bg-surface-sunken text-fg-body">
                    {recycleBinCount}
                  </span>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBarWithTabs;
